import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Lock, Sparkles, X } from "lucide-react";
import { useAuth } from "@/config/auth";

const DISMISS_KEY = "hypes_psi_adv_banner_dismissed";

/**
 * AdvancedModeBanner — a prominent, persistent CTA shown on every Basic Mode
 * page that invites users to upgrade to Advanced Mode.
 *
 * - Renders only on /basic/* routes (controlled by Layout via `show` prop)
 * - Glassmorphism, gradient accents, subtle hover lift
 * - Dismissable for the session (resets per browser session, not permanent)
 */
export default function AdvancedModeBanner({ compact = false }) {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {}
  }, []);

  if (dismissed) return null;

  const handleClick = () => {
    try { localStorage.setItem("upsi_mode", "advanced"); } catch {}
    if (user) navigate("/advanced");
    else navigate("/login", { state: { from: "/advanced" } });
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setDismissed(true);
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#1B291D] to-[#2a3d2e] text-white text-xs font-bold tracking-wide shadow-md hover:shadow-lg hover:scale-[1.02] transition-all"
        data-testid="adv-mode-banner-compact"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#9CCC9F] group-hover:rotate-12 transition-transform" />
        <span>Try Advanced Mode</span>
        <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </button>
    );
  }

  return (
    <div
      onClick={handleClick}
      className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0E1A11] via-[#1B291D] to-[#162B19] text-white px-5 py-4 mb-6 cursor-pointer shadow-lg shadow-[#1B291D]/20 hover:shadow-xl transition-all hover:-translate-y-0.5"
      data-testid="adv-mode-banner"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
    >
      {/* Decorative gradient blobs */}
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#43A047]/30 blur-3xl group-hover:bg-[#43A047]/45 transition-all duration-700" />
      <div className="absolute -bottom-10 left-1/3 w-40 h-40 rounded-full bg-[#43A047]/15 blur-3xl" />

      <div className="relative z-10 flex flex-wrap items-center gap-3 sm:gap-4 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-xl bg-[#43A047]/50 blur-md group-hover:blur-lg transition-all" />
            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-[#43A047] to-[#388E3C] flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm sm:text-[15px] font-semibold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
                Unlock Advanced Mode
              </p>
              <span className="text-[9px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-[#43A047]/20 text-[#9CCC9F] border border-[#43A047]/30">
                Pro
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-white/70 mt-0.5 leading-snug">
              Substage-level evaluation, saved projects, benchmark comparisons &amp; exportable reports.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
            className="group/btn inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white text-[#1B291D] text-xs font-bold hover:bg-[#9CCC9F] transition-all"
          >
            {user ? (isAdmin ? "Open Admin" : "Open Advanced") : "Try Advanced"}
            {!user && <Lock className="w-3 h-3" />}
            <ArrowUpRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
          </button>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="adv-mode-banner-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
